import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;
let pendingPageView: { name: string; uri?: string } | null = null;
let initializationPromise: Promise<void> | null = null;

interface AppInsightsConfig {
	connectionString: string;
}

const EVO_ORGANISATION_ID_KEY = 'evo_organisation_id';
const PLATFORM_USER_ID_KEY = 'platform_user_id';

const initializeWithConnectionString = (connectionString: string): void => {
	if (!connectionString) {
		return;
	}

	if (appInsights) {
		return;
	}

	appInsights = new ApplicationInsights({
		config: {
			connectionString,
			enableAutoRouteTracking: false,
			disableFetchTracking: false,
			disableAjaxTracking: false,
			enableCorsCorrelation: true,
			enableRequestHeaderTracking: true,
			enableResponseHeaderTracking: true,
		},
	});

	appInsights.loadAppInsights();

	// Add telemetry initializer to automatically include custom attributes in all telemetry
	appInsights.addTelemetryInitializer((envelope) => {
		const organisationId = sessionStorage.getItem(EVO_ORGANISATION_ID_KEY);
		const userId = sessionStorage.getItem(PLATFORM_USER_ID_KEY);

		envelope.data = envelope.data || {};

		// Static attributes
		envelope.data['application.name'] = 'Time Track Pro';
		envelope.data['subsystem.name'] = 'Time Track Pro';
		envelope.data['pe.code'] = 'MX-TIME';

		// Dynamic attributes from session context
		if (organisationId) {
			envelope.data.OrganisationId = organisationId;
			envelope.data.WorkspaceOrganisationId = organisationId;
		}
		if (userId) {
			envelope.data.UserId = userId;
			envelope.data.WorkspaceUserId = userId;
		}
	});

	if (pendingPageView) {
		appInsights.trackPageView({
			name: pendingPageView.name,
			uri: pendingPageView.uri ?? window.location.href,
		});
		pendingPageView = null;
	}
};

export const initializeAppInsightsFromConfig = (): Promise<void> => {
	if (initializationPromise) {
		return initializationPromise;
	}

	initializationPromise = (async () => {
		try {
			const response = await fetch('/config/appInsights.config.json');
			if (!response.ok) {
				return;
			}
			const config: AppInsightsConfig = await response.json();
			if (config.connectionString) {
				initializeWithConnectionString(config.connectionString);
			}
		} catch {
			// Config file not found or invalid - silently skip initialization
		}
	})();

	return initializationPromise;
};

export const getAppInsights = (): ApplicationInsights | null => {
	return appInsights;
};

export const trackPageView = (name: string, uri?: string): void => {
	// Custom properties (OrganisationId, UserId) are automatically added
	// by the telemetry initializer when the SDK is initialized
	if (appInsights) {
		appInsights.trackPageView({
			name,
			uri: uri ?? window.location.href,
		});
	} else {
		pendingPageView = { name, uri };
	}
};
